import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiError } from '../api/client';
import { translateApi, type TranslateRunsParams } from '../api/endpoints';
import type { Id, Page, TranslateRun, TranslateRunRequest } from '../types/api';
import { queryKeys, pollIntervalFrom } from './keys';

export function useTranslateRuns(
  params: TranslateRunsParams = {},
): UseQueryResult<Page<TranslateRun>, ApiError> {
  return useQuery<Page<TranslateRun>, ApiError>({
    queryKey: queryKeys.translateRuns(params),
    queryFn: () => translateApi.list(params),
    placeholderData: keepPreviousData,
  });
}

export function useTranslateRun(runId: Id | undefined): UseQueryResult<TranslateRun, ApiError> {
  return useQuery<TranslateRun, ApiError>({
    queryKey: queryKeys.translateRun(runId ?? ''),
    queryFn: () => translateApi.get(runId as Id),
    enabled: Boolean(runId),
    refetchInterval: (query) =>
      isTranslateRunActive(query.state.data) ? pollIntervalFrom(undefined) : false,
    retry: (failureCount, error) => !error.isNotFound && failureCount < 2,
  });
}

export function useStartTranslate() {
  const qc = useQueryClient();
  return useMutation<TranslateRun, ApiError, TranslateRunRequest>({
    mutationFn: (payload) => translateApi.create(payload),
    retry: false,
    onSuccess: (run) => {
      qc.setQueryData(queryKeys.translateRun(run.id), run);
      void qc.invalidateQueries({ queryKey: queryKeys.translateRoot });
    },
  });
}

export function isTranslateRunActive(run: TranslateRun | undefined): boolean {
  return run?.status === 'pending' || run?.status === 'running';
}

export const TRANSLATE_ACCEPT = 'video/*,audio/*,.srt';
